import { useFragment } from '@apollo/client';
import { Stack } from '@mui/material';
import { Partner } from '@provision/entity-types';
import { portalPaths } from '@provision/portal-routes';
import { useEffect, useMemo, useState } from 'react';
import { EntityOpenModeSwitcher, SidePanelTabs } from 'src/components';
import { useClientQuickActions } from 'src/components/ClientComponents/use-client-quick-actions';
import { BlockContainer, BlockHeader } from 'src/components/Layout/named-layout-block.view';

import { gql } from 'src/gql';
import { RecordOpenMode, useGroupRules, UserGroupRules } from 'src/graphql';
import { CustomAttributesSidePanelProvider } from 'src/graphql/entities/custom-attributes/side-panel-provider';
import { SidePanelPersonalizationProvider } from 'src/graphql/entities/side-panel-personalization';
import { CrossPartnerUserSettingsProvider } from 'src/graphql/entities/side-panel-personalization/cross-partner-user-settings-provider';
import { EntityNames } from 'src/graphql/types';
import { clientSidePanelSurface } from 'src/pages/Clients/SidePanel/client-surface';
import { PartnerHeader } from 'src/pages/Conversations/SidePanel/partner-header';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useCustomView } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';
import { ClientDetailsTabs } from './client-details-tabs';
import { ClientTabContent } from './client-tab-content';

const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Client Details';

const ClientSidePanelFragment = gql(`
  fragment ClientSidePanel_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    isDeleted @client
    primaryEmail {
      id
      value
    }
    partner {
      id
      partnerId
      businessName
      timeZone
    }
    ...ClientTabContent_Client
    ...UseClientQuickActions_Client
  }
`);

interface ClientSidePanelProps {
  clientId: string;
  onClose?: () => void;
  extendedView?: boolean;
}

export const ClientSidePanel = ({ onClose, clientId, extendedView }: ClientSidePanelProps) => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const [activeTab, setActiveTab] = useState(ClientDetailsTabs.Client);
  const { groupRules } = useGroupRules();
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const { data: client, complete } = useFragment({
    fragment: ClientSidePanelFragment,
    fragmentName: 'ClientSidePanel_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const customViewId = useCustomView(EntityNames.Client)?.customViewId;

  const { setModalProps: setAddSiteModalProps } = useModalMethods(Modals.AddSiteModal);

  useEffect(() => {
    if (!complete) {
      return;
    }
    return setAddSiteModalProps({
      client: {
        partnerId: client.partnerId,
        id: client.clientId,
        name: client.fullName,
        email: client.primaryEmail?.value,
      },
    });
  });

  useClientQuickActions({
    clientId,
  });

  const tabs = useMemo(
    () =>
      [
        {
          value: ClientDetailsTabs.Client,
          label: 'Client',
        },
        {
          value: ClientDetailsTabs.Partner,
          label: 'Partner',
          skip: !canSeePartnersTab,
          disabled: !client.partner,
        },
      ].filter((tab) => !tab.skip),
    [canSeePartnersTab, client.partner],
  );

  if (!complete) {
    return null;
  }

  return (
    <CrossPartnerUserSettingsProvider>
      <CustomAttributesSidePanelProvider
        entity="Client"
        entityId={client.clientId}
        partnerId={client.partner?.partnerId ?? client.partnerId}
        canEdit={!client.isDeleted}
      >
        <SidePanelPersonalizationProvider surface={clientSidePanelSurface}>
          <BlockContainer
            sx={{
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Stack direction="column" spacing={1} width="100%" minHeight={0}>
              {!extendedView && !isMobile && (
                <BlockHeader sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                  <EntityOpenModeSwitcher
                    openMode={RecordOpenMode.FullPage}
                    onClick={() => {
                      navigate(portalPaths.client.full(clientId), {
                      state: { customViewId },
                    });
                  }}
                  />
                </BlockHeader>
            )}
            <PartnerHeader
              displayPartnerInfo={canAccessToPartnerTab}
              // TODO(gql-migrate): PartnerHeader still consumes the legacy Partner shape.
              partner={(client.partner ?? undefined) as Partial<Partner> | undefined}
              closeDetailsPanelTooltip={CLOSE_DETAILS_PANEL_TOOLTIP}
              onClose={onClose}
            />
              <SidePanelTabs
                key={clientId}
                sx={{
                  marginTop: (theme) => theme.utils.important(0),
                  overflow: 'auto',
                }}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={tabs}
                showLabel
                hasBorderTop={false}
              >
                <ClientTabContent
                  activeTab={activeTab}
                  clientId={clientId}
                  extendedView={extendedView}
                />
              </SidePanelTabs>
            </Stack>
          </BlockContainer>
        </SidePanelPersonalizationProvider>
      </CustomAttributesSidePanelProvider>
    </CrossPartnerUserSettingsProvider>
  );
};
